'use client'

import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'

import type { User } from '@/lib/types'
import { logoutAccount } from '@/lib/db/user'
import { LINKEDIN_URL, X_URL, USER_DEFAULT_COIN } from '@/lib/constant'
import { useCurrentUser } from '@/hooks/use-current-user'
import { TwitterIcon, LinkedInIcon } from '@/components/icons'
import sendToMixpanel from '@/lib/mixpanel/sendToMixpanel'
import { SidebarMenuBtn } from './ai-chat/sidebar-menu/profile'
import { LoginStyleBtn } from './home-v2/components/LoginStyleBtn'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { GetFreeCreditButton } from '@/components/get-free-credit-button'

type ProfileMenuProps = {
  user: User | null
  showSocialMediaLinks?: boolean
  transparent?: boolean
}

export function ProfileMenu({
  user: fallbackData,
  showSocialMediaLinks = true,
  transparent = false,
}: ProfileMenuProps) {
  const router = useRouter()
  const t = useTranslations('profile-menu')
  const { data: user } = useCurrentUser({ fallbackData })
  const [showUpgradeReminder, setShowUpgradeReminder] = useState(false)

  useEffect(() => {
    if (!user) return

    const lastReminderDate = localStorage.getItem('lastUpgradeReminder')
    const today = new Date().toDateString()

    if (
      Number(user.coin) < 10 &&
      Number(user.coin) !== USER_DEFAULT_COIN &&
      lastReminderDate !== today
    ) {
      setShowUpgradeReminder(true)
      localStorage.setItem('lastUpgradeReminder', today)

      // Hide the reminder after 5 seconds
      const timer = setTimeout(() => {
        setShowUpgradeReminder(false)
      }, 5000)

      // Cleanup timeout when component unmounts
      return () => clearTimeout(timer)
    }
  }, [user])

  async function onLogout() {
    const success = await logoutAccount()
    if (success) {
      router.push('/login')
    }
    sendToMixpanel(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_LOGOUT,
      {},
      user?._id.toString(),
    )
  }

  if (!user) {
    return (
      <div className="flex items-center gap-4">
        {showSocialMediaLinks && (
          <div className="hidden md:block">
            <SocialMediaLinks />
          </div>
        )}
        <LoginStyleBtn
          onClick={() => {
            router.push('/login')
            sendToMixpanel(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_LOGIN, {}, '')
          }}
        >
          <span className="text-white text-sm relative z-10">{t('login')}</span>
          <div className="absolute inset-[-2px] bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine-soft" />
        </LoginStyleBtn>
      </div>
    )
  }

  // show in two decimal places
  const userBalance = (user.coin || 0).toFixed(2)
  const showBalanceAndUpgrade =
    Number(userBalance) < 10 && Number(userBalance) !== USER_DEFAULT_COIN

  return (
    <div className="flex items-center gap-4">
      {/* {showBalanceAndUpgrade && (
        <>
          <div className="hidden md:block">
            <div className="text-sm text-stone-600">
              {t('balance')}:{' '}
              <strong className="text-[1.3em]">{userBalance}</strong>{' '}
              {t('credits')}
            </div>
          </div>
          <div className="hidden md:block relative">
            <Button
              variant="reminder"
              className="text-sm"
              onClick={() => router.push('/pricing')}
            >
              {t('upgrade')}
            </Button>
            {showUpgradeReminder && (
              <div className="absolute top-12 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-4 py-2 rounded-lg shadow-lg border border-stone-200 z-50">
                <div className="absolute top-[-8px] left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-stone-200 transform rotate-45" />
                <div className="flex items-center gap-2">
                  <span className="text-yellow-500">⚠</span>
                  <span className="text-sm">
                    {t('low-credits-warning')}
                    <br />
                    {t('recharge-reminder')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )} */}
      {/* <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="inline-flex items-center gap-1.5 text-xs bg-brand-light rounded-sm text-brand focus:outline-none disabled:cursor-not-allowed hover:bg-brand-light/80 group">
            <Info className="w-4 h-4 group-hover:text-white" />
            <span className="group-hover:text-white text-[14px]">
              {t('need-help')}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            className="cursor-pointer flex items-center gap-2 bg-brand-light/10 hover:bg-brand-light/20 rounded-md mb-1"
            onClick={() => {
              window.open(DISCORD_URL, '_blank')
            }}
          >
            <DiscordLineIcon className="w-4 h-4 text-brand" />
            <span className="font-medium">{t('community-support')}</span>
            <span className="ml-auto text-[10px] bg-brand text-white px-1.5 py-0.5 rounded-full">
              24/7
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              window.open(FEATUREBASE_URL, '_blank')
            }}
          >
            <CircleHelp className="w-4 h-4" />
            {t('help-center')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu> */}

      {showSocialMediaLinks && (
        <div className="hidden md:block">
          <SocialMediaLinks />
        </div>
      )}
      <SidebarMenuBtn user={user} transparent={transparent} />
    </div>
  )
}

function SocialMediaLinks() {
  return (
    <div className="flex items-center gap-3">
      {/* <Link
        href={DISCORD_URL}
        target="_blank"
        className="flex items-center justify-center w-8 h-8 bg-transparent border border-black/10 rounded-[8px] hover:bg-stone-100 transition-colors duration-200"
      >
        <DiscordIcon className="w-4 h-4 text-[#5865F2]" />
      </Link> */}
      <Link
        href={LINKEDIN_URL}
        target="_blank"
        className="flex items-center justify-center w-8 h-8 bg-transparent border border-black/10 rounded-[8px] hover:bg-stone-100 transition-colors duration-200"
      >
        <LinkedInIcon className="w-4 h-4 text-[#0077B5]" />
      </Link>
      <Link
        href={X_URL}
        target="_blank"
        className="flex items-center justify-center w-8 h-8 bg-black border border-black/10 rounded-[8px] hover:bg-black/80 transition-colors duration-200"
      >
        <TwitterIcon className="w-4 h-4 text-white" />
      </Link>
      <GetFreeCreditButton />
    </div>
  )
}
